import React, { useState, useEffect } from "react";
import styled from "styled-components";

const Bar = styled.div`
  width : 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  padding: 8px 25px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 6px rgba(35,62,88,0.1);
`;

const FlexItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.85rem;
  color: #21325b;
  margin-bottom: 3px;
  user-select: none;
`;

const Input = styled.input`
  padding: 7px 10px;
  font-size: 0.98rem;
  border: 1.5px solid #ccc;
  border-radius: 8px;
  font-family: 'Segoe UI', 'Inter', Arial, sans-serif;
  background: #fff;
  &:focus {
    border-color: #3772FF;
    outline: none;
  }
`;

const TextInput = styled(Input)`
  min-width: 280px;   /* Longer search input */
`;

const ShortInput = styled(Input)`
  min-width: 100px;   /* Compact other inputs */
`;

const Select = styled.select`
  padding: 7px 10px;
  font-size: 0.98rem;
  border: 1.5px solid #ccc;
  border-radius: 8px;
  font-family: 'Segoe UI', 'Inter', Arial, sans-serif;
  background: #fff;
  &:focus {
    border-color: #3772FF;
    outline: none;
  }
`;

const ShortSelect = styled(Select)`
  min-width: 100px;  /* Compact selects */
`;

const Button = styled.button`
  background: #3772FF;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(35,62,88,0.05);
  user-select: none;
  margin-left: 4px;
  margin-bottom: 2px;
  transition: background-color 0.3s ease;
  min-width: 70px;
  align-self: center;
  &:hover {
    background: #245bb9;
  }
`;

const ExportButton = styled.button`
  background: #e84848;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 5px 9px;
  font-weight: 700;
  font-size: 0.85rem;
  min-width: 55px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  margin-left: 4px;
  margin-bottom: 2px;
  user-select: none;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #c41c1c;
  }
  svg { 
    font-size: 1.2em;
    flex-shrink: 0;
    margin-right: 0;
  }
`;

export default function FilterBar({
  onTextFilter,
  onDateFilter,
  onSortChange,
  onResetFilters,
  onExportPDF,
  onExportExcel,
  sortFields = [],
  placeholder = "Search...",
}) {
  const [text, setText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Debounce for text filter
  useEffect(() => {
    if (typeof onTextFilter === "function") {
      const handler = setTimeout(() => {
        onTextFilter(text.trim() === "" ? null : text.trim());
      }, 300);
      return () => clearTimeout(handler);
    }
  }, [text, onTextFilter]);

  // Date filter effect
  useEffect(() => {
    if (typeof onDateFilter === "function") {
      const fDate = fromDate === "" ? null : fromDate;
      const tDate = toDate === "" ? null : toDate;
      onDateFilter(fDate, tDate);
    }
  }, [fromDate, toDate, onDateFilter]);

  // Sort filter effect
  useEffect(() => {
    if (typeof onSortChange === "function") {
      if (sortField === "") {
        onSortChange(null, null);
      } else {
        onSortChange(sortField, sortOrder);
      }
    }
  }, [sortField, sortOrder, onSortChange]);

  // Reset filters handler
  const handleReset = () => {
    setText("");
    setFromDate("");
    setToDate("");
    setSortField("");
    setSortOrder("asc");
    if (typeof onResetFilters === "function") {
      onResetFilters();
    }
  };

  return (
    <Bar>
      <FlexItem>
        <Label htmlFor="textFilter">Search</Label>
        <TextInput
          id="textFilter"
          type="text"
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </FlexItem>
      <FlexItem>
        <Label htmlFor="fromDate">From</Label>
        <ShortInput
          id="fromDate"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
      </FlexItem>
      <FlexItem>
        <Label htmlFor="toDate">To</Label>
        <ShortInput
          id="toDate"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
      </FlexItem>
      <FlexItem>
        <Label htmlFor="sortField">Sort By</Label>
        <ShortSelect
          id="sortField"
          value={sortField}
          onChange={(e) => {
            setSortField(e.target.value);
            if (e.target.value === "") setSortOrder("asc"); // reset order when clearing field
          }}
        >
          <option value="">Select field</option>
          {sortFields.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </ShortSelect>
      </FlexItem>
      {sortField && (
        <FlexItem>
          <Label htmlFor="sortOrder">Order</Label>
          <ShortSelect
            id="sortOrder"
            value={sortOrder}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "asc" || val === "desc") setSortOrder(val);
            }}
          >
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </ShortSelect>
        </FlexItem>
      )}
      <FlexItem>
        <Label>&nbsp;</Label>
        <Button onClick={handleReset} type="button" aria-label="Reset all filters">
          Reset
        </Button>
      </FlexItem>
      <FlexItem style={{ flexDirection: "row", gap: "4px", alignItems: "center" }}>
        <ExportButton
          title="Export to PDF"
          onClick={typeof onExportPDF === "function" ? onExportPDF : undefined}
          type="button"
          aria-label="Export to PDF"
        >
          <svg width="18" height="18" fill="none" aria-hidden>
            <rect width="12" height="16" x="3" y="1" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M7 6.5h4M7 9h4M7 11.5h2" stroke="currentColor" strokeWidth="1.2" />
            <rect x="0" y="0" width="18" height="18" fill="none" />
          </svg>
          PDF
        </ExportButton>

        <ExportButton
          title="Export to Excel"
          onClick={typeof onExportExcel === "function" ? onExportExcel : undefined}
          type="button"
          aria-label="Export to Excel"
          style={{ background: "#45a845" }}
        >
          <svg width="18" height="18" fill="none" aria-hidden>
            <rect width="12" height="16" x="3" y="1" rx="2" fill="none" stroke="#fff" strokeWidth="1.3" />
            <path d="M7 7.5l4 5M11 7.5l-4 5" stroke="#fff" strokeWidth="1.2" />
            <rect x="0" y="0" width="18" height="18" fill="none" />
          </svg>
          XLS
        </ExportButton>
      </FlexItem>
    </Bar>
  );
}
